<script setup>
/*
========================================================================
StepReport_Modal_EditRoom.vue

Slide-in modal for editing room details from the Report step.
Uses BaseModalPanel for consistent modal behavior (backdrop, escape
handling, focus trap, slide-in animation).

Flow:
- On open: Set store.showRoomForm = true (enables form buttons)
- On "Save and Calculate" button: Form updates store, closes, we recalculate
- On "Cancel" button: Form resets its local data, closes (store unchanged)
- On Escape/backdrop/X: Reset form data, close (store unchanged)

Note: Since realtime=false, form changes are local until Save is clicked.
The store is only modified when "Save and Calculate" is clicked.
========================================================================
*/
import { ref, watch } from "vue";
import { useMainStore } from "@/stores/store.js";
import BaseModalPanel from "./utils/BaseModalPanel.vue";
import RoomDataForm from "./RoomDataForm.vue";

const store = useMainStore();

/*
------------------------------------------------------------------------
Props
------------------------------------------------------------------------
*/
const props = defineProps({
	// Controls modal visibility
	show: {
		type: Boolean,
		required: true,
	},
	// Function to close the modal (provided by parent)
	fnCancelModal: {
		type: Function,
		required: true,
	},
});

/*
------------------------------------------------------------------------
Local State
------------------------------------------------------------------------
*/
// Flag to prevent watcher from double-closing
const isClosingFromModal = ref(false);

/*
------------------------------------------------------------------------
Watch for modal opening - enable form buttons
------------------------------------------------------------------------
*/
watch(
	() => props.show,
	(newValue) => {
		if (newValue) {
			// Modal is opening - enable the form buttons
			store.showRoomForm = true;
			isClosingFromModal.value = false;
		}
	},
);

/*
------------------------------------------------------------------------
Watch for form button clicks (detected via store.showRoomForm)
The form sets this to false when Save or Cancel is clicked.
------------------------------------------------------------------------
*/
watch(
	() => store.showRoomForm,
	async (newValue, oldValue) => {
		// Skip if we're closing from modal-level action (Escape/backdrop/X)
		if (isClosingFromModal.value) {
			return;
		}

		// Only respond when going from true to false while modal is open
		if (oldValue === true && newValue === false && props.show) {
			// Form button was clicked - close the modal
			props.fnCancelModal();

			// Show loading indicator and trigger recalculation
			store.overlays.loading = true;
			await store.calculateProductsBatch();
			store.overlays.loading = false;
		}
	},
);

/*
------------------------------------------------------------------------
Modal Handlers
------------------------------------------------------------------------
*/

// Handle modal cancel (backdrop click, X button, or escape key)
// This is different from the form's Cancel button
const handleCancel = () => {
	// Set flag to prevent watcher from also triggering
	isClosingFromModal.value = true;

	// Reset the form flag
	store.showRoomForm = false;

	// Tell the form to sync from store (resets any local changes)
	store.syncRoomData = true;

	// Close the modal
	props.fnCancelModal();
};

// Handle escape key
const handleEscape = () => {
	handleCancel();
};
</script>

<template>
	<!--
	========================================================================
	Edit Room Modal
	Wrapped in BaseModalPanel for consistent modal behavior (backdrop,
	escape handling, focus trap).
	========================================================================
	-->
	<BaseModalPanel
		:show="show"
		:on-close="handleCancel"
		:on-escape="handleEscape"
		:show-backdrop="true"
		:close-on-backdrop-click="false"
		:show-close-button="true"
		:topmost="true"
		max-width="max-w-[800px]"
	>
		<!--
		----------------------------------------------------------------
		Scrollable container for form
		----------------------------------------------------------------
		-->
		<div class="flex h-full flex-col overflow-y-auto">
			<!--
			----------------------------------------------------------------
			Header (sticky within scroll container)
			----------------------------------------------------------------
			-->
			<div
				class="sticky top-0 z-10 flex flex-col justify-between border-b border-gray-200 bg-white px-8 py-6"
			>
				<h2 class="text-2xl font-bold text-black">Edit Room Details</h2>
				<p class="text-gray-700">
					Update your room dimensions, materials, and usage type:
				</p>
			</div>

			<!--
			----------------------------------------------------------------
			Room Data Form Content
			----------------------------------------------------------------
			-->
			<div class="flex-1 px-8 py-6">
				<RoomDataForm :realtime="false" />
			</div>
		</div>
	</BaseModalPanel>
</template>
